import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import CheckboxTsoPq from '@components/Checkbox/CheckboxTsoPq';
import ChipSelectorTsoPq from '@components/ChipSelector/ChipSelectorTsoPq';
import DatepickerTsoPq from '@components/Datepicker/DatepickerTsoPq';
import { SidePanelTsoPq } from '@components/Grid/SidePanelTsoPq';
import SelectTsoPq from '@components/Select/SelectTsoPq';
import TreeAutocompleteTsoPq from '@components/TreeAutocomplete/TreeAutocompleteTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { parseSubstationDevices, TreeListItemTsoPqProps } from '@custom-types/TreeTypes';
import {
  DeviceProps,
  StyledButtonWrapper,
  StyledLineWrapper,
  StyledMeasurementDeviceTitle,
  StyledWrapper,
} from '@pages/MeasurementVisualization/Section/MeasurementVisualizationParameters';
import { StyledSectionWrapper } from '@pages/MeasurmentExport/MeasurmentExportStyles';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';

import { useSidePanel } from '@components/Grid/GridTsoPq';
import { Box } from '@mui/material';
import { findDiff } from '@pages/MeasurementVisualization/utils';
import { useGetLimitSetGetAllQuery } from '@redux/apiSlices/limitSetApi';
import {
  ApiCommonExternalDtoMeasurementParameterExternalDto,
  useGetMeasurementParameterGetAllQuery,
} from '@redux/apiSlices/measurementParameterApi';
import { useLazyGetSubstationGetSubstationDeviceHierarchyQuery } from '@redux/apiSlices/substationApi';
import { useAppSelector } from '@redux/hooks';
import {
  AppliedParameters,
  DeviceDetailsType,
  Payload,
  selectAppliedParameters,
  SelectedParameterDetails,
  setAppliedParameters,
  setDeviceDetails,
  setParametersDetails,
  setParametersPayload,
} from '@redux/slices/pqIndexSlice';
import { useFlattenTreeProps } from '@utils/hooks/useFlattenTreeProp';
import { useLtreeTreeListFromObjects } from '@utils/hooks/useTransfromLtreeInHyerarchy';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { StyledCheckboxContainer, StyledLabel } from '../PqIndexStyle';

const limitSetNames = {
  IEC_61000: ['iec 61000', 'planungspegel', 'planning level'],
  VDE_ARN_4130: ['vde arn 4130', '(tar)'],
  EN_50160: ['en 50160', 'pq index'],
};

type MeasurementParameterUsageKey = 'isUsedForPqIndex' | 'isUsedForPlanningLevel' | 'isUsedForTAR';

const getMeasurementParameterUsageKey = (
  limitSetLabel: string | undefined
): MeasurementParameterUsageKey | undefined => {
  const normalizedLabel = limitSetLabel?.trim().toLowerCase();

  if (!normalizedLabel) {
    return undefined;
  }

  if (limitSetNames.EN_50160.some((part) => normalizedLabel.includes(part))) {
    return 'isUsedForPqIndex';
  }

  if (limitSetNames.IEC_61000.some((part) => normalizedLabel.includes(part))) {
    return 'isUsedForPlanningLevel';
  }

  if (limitSetNames.VDE_ARN_4130.some((part) => normalizedLabel.includes(part))) {
    return 'isUsedForTAR';
  }

  return undefined;
};

const getSelectableParameterIds = (nodes: TreeListItemTsoPqProps[] | undefined): Set<string> => {
  const selectableParameterIds = new Set<string>();

  const visit = (items: TreeListItemTsoPqProps[] = []) => {
    for (const item of items) {
      if (item.type === 'item') {
        selectableParameterIds.add(item.id);
      }

      if (item.options) {
        visit(item.options);
      }
    }
  };

  visit(nodes);
  return selectableParameterIds;
};

export type Option = {
  id: number;
  value?: string;
  label: string;
};

export type TsosStateProps = {
  id: number;
  name: string;
  active: boolean;
  abbreviation: string;
};

const PqIndexParameters = () => {
  const { handleOpen } = useSidePanel();
  const [tsoDeviceOptions, setTsoDeviceOptions] = useState<{
    [key: number]: TreeListItemTsoPqProps[] | undefined;
  }>({});
  const [activeTsos, setActiveTsos] = useState<TsosStateProps[] | []>([]);
  const [selectedDevices, setSelectedDevices] = useState<{ [key: string]: string[] } | undefined>(undefined);
  const [activeTsoName, setActiveTsoName] = useState<string>('');
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [selectedParameters, setSelectedParameters] = useState<string[] | undefined>(undefined);
  const [parameterOptions, setParameterOptions] = useState<TreeListItemTsoPqProps[] | undefined>([]);
  const [limitSetOptions, setLimitSetOptions] = useState<Option[] | undefined>([]);
  const [selectedLimitSet, setSelectedLimitSet] = useState<Option | null | undefined>(undefined);
  const [checkedHarmonics, setCheckedHarmonics] = useState<{ [key: string]: boolean } | undefined>(undefined);
  const deviceType = 2;

  const appliedParameters = useAppSelector(selectAppliedParameters);

  // validations
  const [mandatoryFieldsStatus, setMandatoryFieldsStatus] = useState<{
    activeTsos: boolean | undefined;
    startDate: boolean | undefined;
    endDate: boolean | undefined;
    limitset: boolean | undefined;
    selectedParameters: boolean | undefined;
    selectedDevices: { [key: string]: boolean | undefined };
  }>({
    activeTsos: undefined,
    startDate: undefined,
    endDate: undefined,
    limitset: undefined,
    selectedParameters: undefined,
    selectedDevices: {},
  });

  const prevActiveTsosRef = useRef<TsosStateProps[]>([]);
  const latesetParametersValues = useRef<AppliedParameters>({
    tsos: appliedParameters.tsos,
    start: appliedParameters.start,
    end: appliedParameters.end,
    limitSet: appliedParameters.limitSet,
    parameters: appliedParameters.parameters,
    devices: appliedParameters.devices,
  });

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [triggerGetDeviceHierarchy] = useLazyGetSubstationGetSubstationDeviceHierarchyQuery();
  const { data: tsos, isSuccess } = useGetTsoGetAllQuery();
  const { data: measurementParameters } = useGetMeasurementParameterGetAllQuery();
  const { data: limitSet } = useGetLimitSetGetAllQuery();
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleActiveTsosChange = (tsos: TsosStateProps[]) => {
    const lastActiveTsoName = tsos.at(-1)?.name ?? '';

    setSelectedDevices((prev) => {
      const tsosWithSelectedDevices = prev ? Object.keys(prev) : [];
      const selectedTsosIds = tsos.map((tso) => `${tso.id}`);
      const unselectedTsos = tsosWithSelectedDevices.filter((tso) => !selectedTsosIds.includes(tso));
      const prevSelectedDevices = { ...prev };

      (unselectedTsos ?? []).forEach((tso) => {
        if (Object.hasOwn(prevSelectedDevices, tso)) {
          delete prevSelectedDevices[tso];
        }
      });

      return prevSelectedDevices;
    });

    setActiveTsos(tsos);
    setActiveTsoName(lastActiveTsoName);
    latesetParametersValues.current.tsos = tsos;

    const mandatorySelectedDevices = Object.assign({}, ...tsos.map((tso) => ({ [tso.id]: selectedDevices?.[tso.id] })));

    setMandatoryFieldsStatus((prev) => ({
      ...prev,
      activeTsos: formSubmitted ? !!tsos.length : prev.activeTsos,
      selectedDevices: mandatorySelectedDevices,
    }));
  };

  const handleStartChange = (date: dayjs.Dayjs) => {
    setStartDate(dayjs(date).format('YYYY-MM-DD HH:mm'));
    latesetParametersValues.current.start = dayjs(date).format('YYYY-MM-DD HH:mm');
    setMandatoryFieldsStatus((prev) => ({ ...prev, startDate: !!date }));
  };

  const handleEndChange = (date: dayjs.Dayjs) => {
    setEndDate(dayjs(date).format('YYYY-MM-DD HH:mm'));
    latesetParametersValues.current.end = dayjs(date).format('YYYY-MM-DD HH:mm');
    setMandatoryFieldsStatus((prev) => ({ ...prev, endDate: !!date }));
  };

  const handleParametersChange = (selectedParameters: string[]) => {
    setSelectedParameters(selectedParameters);
    selectedParameters.length === 0 && setCheckedHarmonics(undefined);
    latesetParametersValues.current.parameters = selectedParameters;
    selectedParameters.length > 0 &&
      setMandatoryFieldsStatus((prev) => ({ ...prev, selectedParameters: !!selectedParameters.length }));
  };

  const handleDevicesChange = (id: number, selectedDevices: string[], checkedDevices: string[]) => {
    if (id && selectedDevices.length > 0) {
      setSelectedDevices((prev) => {
        latesetParametersValues.current.devices = {
          ...prev,
          [id]: selectedDevices,
        };

        return {
          ...prev,
          [id]: selectedDevices,
        };
      });
    }

    if (id && selectedDevices.length === 0) {
      setSelectedDevices((prev) => {
        latesetParametersValues.current.devices = {
          ...prev,
          [id]: selectedDevices,
        };

        return {
          ...prev,
          [id]: selectedDevices,
        };
      });
    }

    setMandatoryFieldsStatus((prev) => ({
      ...prev,
      selectedDevices: { ...mandatoryFieldsStatus.selectedDevices, [id]: selectedDevices },
    }));
  };

  const generateDevicesDetails = (): {
    [key: number]: DeviceDetailsType;
  } => {
    const allTsoDevicesWithTsoId = Object.entries(tsoDeviceOptions).flatMap(([tsoId, array]) =>
      array?.map((substation) => ({ tsoId: Number(tsoId), ...substation }))
    );
    const allSelectedIds = selectedDevices ? Object.values(selectedDevices).flat() : [];
    const allTsoDevices = tsoDeviceOptions ? Object.values(allTsoDevicesWithTsoId).flat() : [];

    let seen: string[] = [];
    let out: { [key: number]: DeviceDetailsType } = {};

    const findLabels = ({
      substationId,
      measurmentPointId,
      deviceId,
    }: {
      substationId: string;
      measurmentPointId: string;
      deviceId: string;
    }) => {
      const foundSubstation = allTsoDevices.filter((substation) => substation?.id === substationId)[0];
      const foundMeasurmentPoint = foundSubstation?.options?.filter(
        (measurmentPoint) => measurmentPoint.id === measurmentPointId
      )[0];
      const foundDeviceLabel = foundMeasurmentPoint?.options?.filter((device) => device.id === deviceId)[0];

      return {
        tsoId: foundSubstation?.tsoId,
        substatinLabel: foundSubstation?.label ?? '',
        measurementPointLabel: foundMeasurmentPoint?.label ?? '',
        deviceLabel: foundDeviceLabel?.label ?? '',
      };
    };

    for (const deviceId of allSelectedIds) {
      if (!seen.includes(deviceId)) {
        const substationId = deviceId.substring(0, deviceId.indexOf('-'));
        const measurmentPointId = deviceId.substring(0, deviceId.lastIndexOf('-'));
        const labels = findLabels({ substationId, measurmentPointId, deviceId });
        out = {
          ...out,
          [Number(deviceId.split('-').at(-1))]: {
            tsoId: labels.tsoId ?? NaN,
            deviceName: labels.deviceLabel,
            measurementPointName: labels.measurementPointLabel,
            substationName: labels.substatinLabel,
          },
        };
        seen = [...seen, deviceId];
      }
    }

    return out;
  };

  const generateParameterDetails = (): { [key: number]: SelectedParameterDetails } => {
    const parameterMap: { [key: number]: SelectedParameterDetails } = {};

    const getFirstWord = (text: string) => text?.trim().split(' ').at(0) ?? '';

    const traverse = (node: any) => {
      if (!node) return;

      if (node.type === 'item') {
        const id = Number(node.entityId);
        if (!Number.isNaN(id)) {
          parameterMap[id] = {
            id: node.id,
            name: node.label,
            category: getFirstWord(node.label),
          };
        }
      }

      if (node.options && Array.isArray(node.options)) {
        for (const child of node.options) {
          traverse(child);
        }
      }
    };

    for (const rootNode of parameterOptions ?? []) {
      traverse(rootNode);
    }

    return parameterMap;
  };

  const handleLimitSetChange = (option: Option | null) => {
    setSelectedLimitSet(option);
    latesetParametersValues.current.limitSet = option;
    setMandatoryFieldsStatus((prev) => ({ ...prev, limitset: !!option }));
  };

  const handleDiscard = () => {
    setFormSubmitted(false);
    setActiveTsos([]);
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedLimitSet(undefined);
    setSelectedParameters(undefined);
    setSelectedDevices(undefined);
    setCheckedHarmonics(undefined);
    setMandatoryFieldsStatus({
      activeTsos: true,
      startDate: true,
      endDate: true,
      limitset: true,
      selectedParameters: true,
      selectedDevices: {},
    });
    dispatch(
      setAppliedParameters({
        tsos: [],
        start: undefined,
        end: undefined,
        limitSet: undefined,
        parameters: undefined,
        devices: undefined,
      })
    );
  };

  const filterParameters = (harmonics?: 'even' | 'odd') => {
    const parameters = measurementParameters?.externalDtos ?? [];
    const measurementParameterUsageKey = getMeasurementParameterUsageKey(selectedLimitSet?.label);
    let filteredParameters: ApiCommonExternalDtoMeasurementParameterExternalDto[] | undefined = parameters;
    if (harmonics)
      filteredParameters = filteredParameters.filter(
        (parameter) =>
          parameter.categoryPath?.split('.')?.includes(harmonics) && parameter.categoryPath?.includes('.harmonics.')
      );

    if (measurementParameterUsageKey) {
      filteredParameters = filteredParameters.filter((parameter) => parameter[measurementParameterUsageKey] === true);
    } else {
      filteredParameters = [];
    }

    return filteredParameters;
  };

  const updateSelectedParameters = (previousParameters: string[], parameters: string[], checked: boolean) => {
    const baseParameters = previousParameters ?? [];
    if (checked) {
      return [...new Set([...baseParameters, ...parameters])];
    } else {
      const nextParametersState = baseParameters.filter((parameter) => !parameters.includes(parameter));
      return nextParametersState;
    }
  };

  const onChangeChecked = ({ checked, harmonics }: { checked: boolean; harmonics: 'even' | 'odd' }) => {
    setCheckedHarmonics((prevChecked) => {
      const newValues = !prevChecked?.[harmonics];
      const nextCheckState = { ...prevChecked, [harmonics]: checked };
      const parameters = filterParameters(harmonics).map((param) => param.technicalName);

      setSelectedParameters((prevSelecteParameters) =>
        updateSelectedParameters(prevSelecteParameters ?? [], parameters, newValues)
      );

      return nextCheckState;
    });
  };

  const getSelectedParametersId = () => {
    const lookup = new Map(
      (measurementParameters?.externalDtos ?? []).map((parameter) => [parameter.technicalName, parameter.id])
    );

    return [...new Set(
      (selectedParameters ?? []).flatMap((technicalName) => {
        const parameterId = lookup.get(technicalName);
        return typeof parameterId === 'number' ? [parameterId] : [];
      })
    )];
  };

  const getDevicesEntityId = () => {
    const nodesProperties = Object.values(tsoDeviceOptions).flatMap(
      (entry) => entry && useFlattenTreeProps(entry, ['id', 'entityId', 'label', 'technicalName', 'type'])
    ) as DeviceProps[];
    const deviceProperties = nodesProperties.filter((device) => Number(device?.type) === deviceType);
    const faltSelectedDevicesId = selectedDevices && Object.values(selectedDevices).flat();
    const lookup = new Map(deviceProperties.map((device) => [device.id, device.entityId]));
    return (faltSelectedDevicesId ?? []).flatMap((id) => {
      const deviceId = lookup.get(id);
      return typeof deviceId === 'number' ? [deviceId] : [];
    });
  };

  const handleSubmit = async () => {
    setFormSubmitted(true);
    const measurementDeviceIds = getDevicesEntityId() as number[];
    const measurementParameters = getSelectedParametersId();

    if (
      startDate &&
      endDate &&
      selectedLimitSet &&
      measurementDeviceIds.length > 0 &&
      measurementParameters.length > 0
    ) {
      const payload: Payload = {
        from: dayjs(startDate).format('YYYY-MM-DD'),
        to: dayjs(endDate).format('YYYY-MM-DD'),
        timeGrouping: 0,
        measurementDeviceGrouping: 0,
        limitSetId: selectedLimitSet?.id,
        apiControllersPqIndexParameters: {
          measurementDeviceIds: measurementDeviceIds,
          measurementParameterIds: measurementParameters,
        },
      };
      dispatch(
        setAppliedParameters({
          tsos: activeTsos,
          start: startDate,
          end: endDate,
          limitSet: selectedLimitSet,
          parameters: selectedParameters,
          devices: selectedDevices,
        })
      );
      dispatch(setParametersPayload(payload));
      dispatch(setDeviceDetails(generateDevicesDetails()));
      dispatch(setParametersDetails(generateParameterDetails()));
      handleOpen(false);
    }

    setMandatoryFieldsStatus({
      activeTsos: activeTsos.length > 0,
      startDate: !!startDate,
      endDate: !!endDate,
      limitset: !!selectedLimitSet,
      selectedParameters: !!(selectedParameters?.length && selectedParameters?.length > 0),
      selectedDevices: Object.fromEntries(
        Object.keys(mandatoryFieldsStatus.selectedDevices).map((tsoId) => [
          tsoId,
          selectedDevices?.[tsoId] === undefined ? false : selectedDevices?.[tsoId].length > 0,
        ])
      ),
    });
  };

  const parameters = useLtreeTreeListFromObjects(filterParameters());

  useEffect(() => {
    setParameterOptions(parameters);
  }, [measurementParameters, selectedLimitSet]);

  useEffect(() => {
    const selectableParameterIds = getSelectableParameterIds(parameterOptions);
    const nextSelectedParameters = (selectedParameters ?? []).filter((parameter) => selectableParameterIds.has(parameter));

    if ((selectedParameters ?? []).length !== nextSelectedParameters.length) {
      setSelectedParameters(nextSelectedParameters);
      latesetParametersValues.current.parameters = nextSelectedParameters;
    }
  }, [parameterOptions, selectedParameters]);

  useEffect(() => {
    if (activeTsos) {
      const diff = findDiff({ activeTsos: activeTsos, prevActiveTsosRef: prevActiveTsosRef });

      const fetchDevices = async (id: number) => {
        if (isSuccess && tsos?.externalDtos?.length > 0) {
          try {
            const result = await triggerGetDeviceHierarchy(id, true);
            const parsedResult = result.data?.externalDtos && parseSubstationDevices(result.data?.externalDtos);
            parsedResult && setTsoDeviceOptions((prev) => ({ ...prev, [id]: parsedResult }));
          } catch (error) {
            console.error(error);
          }
        }
      };
      if (!Object.keys(tsoDeviceOptions).includes(`${diff[0]?.id}`)) {
        prevActiveTsosRef.current.length < activeTsos.length && fetchDevices(diff[0].id);
      }

      prevActiveTsosRef.current = activeTsos;
    }
  }, [activeTsos, activeTsoName]);

  useMemo(() => {
    setActiveTsos(appliedParameters.tsos);
    setStartDate(appliedParameters.start);
    setEndDate(appliedParameters.end);
    setSelectedLimitSet(appliedParameters.limitSet);
    setSelectedDevices(appliedParameters.devices);
    setSelectedParameters(appliedParameters.parameters);
  }, [
    appliedParameters.tsos,
    appliedParameters.start,
    appliedParameters.end,
    appliedParameters.limitSet,
    appliedParameters.parameters,
    appliedParameters.devices,
  ]);

  useEffect(() => {
    if (limitSet?.externalDtos) {
      const parsedLimitSets = limitSet.externalDtos.map((limit) => ({
        id: limit.id,
        value: limit.id,
        label: limit.name,
      })) as Option[];
      setLimitSetOptions(parsedLimitSets);
    }
  }, [limitSet]);

  useEffect(() => {
    return () => {
      dispatch(
        setAppliedParameters({
          tsos: latesetParametersValues.current.tsos,
          start: latesetParametersValues.current.start,
          end: latesetParametersValues.current.end,
          limitSet: latesetParametersValues.current.limitSet,
          parameters: latesetParametersValues.current.parameters,
          devices: latesetParametersValues.current.devices,
        })
      );
    };
  }, [dispatch]);

  return (
    <SidePanelTsoPq
      sectionTitle={t('pqIndex.parameters.sectionTitle')}
      firstSlot={
        <StyledWrapper>
          <StyledSectionWrapper>
            <ChipSelectorTsoPq
              customWidth="30.5rem"
              label={t('pqIndex.parameters.tsoSelect')}
              chips={tsos?.externalDtos ?? []}
              activeChips={activeTsos}
              handleChange={handleActiveTsosChange}
              multiple
              mandatory
              error={mandatoryFieldsStatus.activeTsos === false}
              supportingText={
                mandatoryFieldsStatus.activeTsos === false
                  ? `${t('pqIndex.parameters.tsoSelect')} ${t('general.error.required')}`
                  : ''
              }
            ></ChipSelectorTsoPq>
            <StyledLineWrapper>
              <DatepickerTsoPq
                label={t('pqIndex.parameters.start')}
                type={'datepicker'}
                width="15rem"
                enableFutureDates={false}
                date={startDate}
                handleChange={handleStartChange}
                mandatory
                error={mandatoryFieldsStatus.startDate === false}
                supportingText={
                  mandatoryFieldsStatus.startDate === false
                    ? `${t('pqIndex.parameters.start')} ${t('general.error.required')}`
                    : ''
                }
              />
              <DatepickerTsoPq
                label={t('pqIndex.parameters.end')}
                type="datepicker"
                width="15rem"
                enableFutureDates={false}
                handleChange={handleEndChange}
                date={endDate}
                minDateTime={dayjs(startDate)}
                mandatory
                error={mandatoryFieldsStatus.endDate === false}
                supportingText={
                  mandatoryFieldsStatus.endDate === false
                    ? `${t('pqIndex.parameters.end')} ${t('general.error.required')}`
                    : ''
                }
              />
            </StyledLineWrapper>
            <SelectTsoPq
              customwidth="31.5rem"
              options={limitSetOptions ?? []}
              label={t('pqIndex.parameters.limitSet')}
              placeholder={t('general.error.valueSelect')}
              handleChange={handleLimitSetChange}
              value={selectedLimitSet ?? ({} as Option)}
              mandatory
              error={mandatoryFieldsStatus.limitset === false}
              supportingText={
                mandatoryFieldsStatus.limitset === false
                  ? `${t('pqIndex.parameters.limitSet')} ${t('general.error.required')}`
                  : ''
              }
            />
            <Box>
              <StyledLabel typographyType={'labelSM'}>{t('pqIndex.parameters.quickselectLabel')}</StyledLabel>
              <StyledLineWrapper>
                <StyledCheckboxContainer>
                  <CheckboxTsoPq
                    checked={!!checkedHarmonics?.even}
                    disabled={!selectedLimitSet?.id}
                    onChange={(event) => {
                      onChangeChecked({ checked: event.target.checked, harmonics: 'even' });
                    }}
                  />
                  <TypographyTsoPq typographyType={'bodyTextSM'}>
                    {t('pqIndex.parameters.allEvenHarmonics')}
                  </TypographyTsoPq>
                </StyledCheckboxContainer>
                <StyledCheckboxContainer>
                  <CheckboxTsoPq
                    checked={!!checkedHarmonics?.odd}
                    disabled={!selectedLimitSet?.id}
                    onChange={(event) => {
                      onChangeChecked({ checked: event.target.checked, harmonics: 'odd' });
                    }}
                  />
                  <TypographyTsoPq typographyType={'bodyTextSM'}>
                    {t('pqIndex.parameters.allOddHarmonics')}
                  </TypographyTsoPq>
                </StyledCheckboxContainer>
              </StyledLineWrapper>
            </Box>
            <TreeAutocompleteTsoPq
              customwidth="31.5rem"
              options={parameterOptions ?? []}
              label={t('pqIndex.parameters.measurmentParameters')}
              placeholder={t('general.error.valueSelect')}
              handleChange={handleParametersChange}
              defaultValue={selectedParameters}
              mandatory
              disabled={!selectedLimitSet?.id}
              error={mandatoryFieldsStatus.selectedParameters === false}
              supportingText={
                mandatoryFieldsStatus.selectedParameters === false
                  ? `${t('pqIndex.parameters.measurmentParameters')} ${t('general.error.required')}`
                  : ''
              }
            />
          </StyledSectionWrapper>
          {activeTsos?.map((tso) => (
            <StyledSectionWrapper key={tso.id} selectDevice>
              <StyledMeasurementDeviceTitle>{tso.name}</StyledMeasurementDeviceTitle>
              <TreeAutocompleteTsoPq
                customwidth="32rem"
                options={tsoDeviceOptions[tso.id] ?? []}
                label={t('pqIndex.parameters.measurementDevice')}
                placeholder={t('general.error.valueSelect')}
                handleChange={(selected, checked) => {
                  handleDevicesChange(tso.id, selected, checked);
                }}
                defaultValue={selectedDevices?.[tso.id as keyof typeof selectedDevices]}
                lowestLevel={3}
                multiple
                mandatory
                error={mandatoryFieldsStatus.selectedDevices[tso.id] === false}
                supportingText={
                  mandatoryFieldsStatus.selectedDevices[tso.id] === false
                    ? `${t('measurementVisualization.parameters.measurementDevice')} ${t('general.error.required')}`
                    : ''
                }
              />
            </StyledSectionWrapper>
          ))}
          <StyledButtonWrapper>
            <ButtonTsoPq
              onClick={() => {
                handleDiscard();
              }}
              sizeVariant="m"
              buttonType="outlinedPrimary"
              textContent={t('pqIndex.parameters.resetAll')}
            ></ButtonTsoPq>
            <ButtonTsoPq
              onClick={() => {
                handleSubmit();
              }}
              sizeVariant="m"
              buttonType="filledPrimary"
              textContent={t('pqIndex.parameters.apply')}
            ></ButtonTsoPq>
          </StyledButtonWrapper>
        </StyledWrapper>
      }
    ></SidePanelTsoPq>
  );
};

export default PqIndexParameters;
